import { AuthData } from '@/auth/types';
import { api } from './api';
import { IUserResponse } from '@/@types/User';

export type AccountLoginPayload = {
  email: string;
  password: string;
  challenge_id?: string;
  code?: string;
};

export type AccountLoginTwoFactorRequired = {
  requires_two_factor: true;
  challenge_id: string;
  expires_in: number;
};

export function getAccountLoginBody(response: unknown) {
  if (!response || typeof response !== 'object') return null;
  const raw = response as Record<string, unknown>;
  const nested = raw.data;
  if (nested && typeof nested === 'object') {
    return nested as Record<string, unknown>;
  }
  return raw;
}

export function isLoginTwoFactorRequired(
  response: unknown,
): response is AccountLoginTwoFactorRequired {
  const body = getAccountLoginBody(response);
  return Boolean(body?.requires_two_factor);
}

export function extractLoginToken(response: unknown): string | null {
  if (!response || typeof response !== 'object') return null;
  const raw = response as Record<string, unknown>;
  const body = getAccountLoginBody(response);
  const token = raw.token ?? body?.token;
  return typeof token === 'string' ? token : null;
}

async function login(data: { email: string; password: string }) {
  const response = await api.post('/v1/authentication/login', {
    ...data,
    email: data.email?.trim().toLowerCase(),
  });

  return response.data;
}

async function accountLogin(data: AccountLoginPayload) {
  const response = await api.post('/v1/authentication/account/login', {
    email: data.email?.trim().toLowerCase(),
    password: data.password,
    ...(data.challenge_id ? { challenge_id: data.challenge_id } : {}),
    ...(data.code ? { code: data.code } : {}),
  });

  return response.data;
}

async function updateLoginTwoFactor(data: {
  enabled: boolean;
  challenge_token: string;
}) {
  const response = await api.patch(
    '/v1/authentication/account/login-two-factor',
    data,
  );

  return response.data;
}

async function selectStore(membershipUuid: string) {
  const response = await api.post(`/v1/authentication/account/select-store/${membershipUuid}`);

  return response.data;
}

async function switchStore(membershipUuid: string) {
  const response = await api.post(`/v1/authentication/account/switch-store/${membershipUuid}`);

  return response.data;
}

async function userInfo() {
  const response = await api.get<IUserResponse>('/v1/authentication/me');

  return response.data.data;
}

async function updateLanguagePreference(languagePreference: string) {
  const response = await api.patch('/v1/authentication/me/language-preference', {
    language_preference: languagePreference,
  });

  return response.data;
}

export const AuthService = {
  login,
  accountLogin,
  updateLoginTwoFactor,
  selectStore,
  switchStore,
  userInfo,
  updateLanguagePreference,
};
